/**
 * Pure section-composition state helpers. Mirrors the backend `PageCompositionService` rules so
 * the editor can validate, reorder and serialize a page's section choices without a round-trip.
 *
 * The state's `order` holds entries: a plain section id (`venue`) or, for an item of a
 * repeatable section, an item reference (`note:k3x9a`). Choices of plain sections live in
 * `sections`; choices of items live in `items`, keyed by reference. `items` is present exactly
 * when the template declares a repeatable section, so the state of other templates keeps its
 * shape.
 */

import {
    CompositionGrid,
    FieldType,
    ITEM_REF_SEPARATOR,
    TABLE_ROW_ID_KEY,
    TABLE_ROW_ID_PATTERN,
    imageCompanionKeys,
} from '../vocabulary.js';
import {newRowId} from './page.core.js';

const SUPPORTED_VERSION = 1;

/** Top-level keys a saved state may carry for a template without a repeatable section. */
const STATE_KEYS = Object.freeze(['version', 'order', 'sections']);

/** Top-level keys a saved state may carry for a template with a repeatable section. */
const REPEATABLE_STATE_KEYS = Object.freeze([...STATE_KEYS, 'items']);

/** Keys a section or item choice may carry. */
const CHOICE_KEYS = Object.freeze(['layout', 'appearance', 'span', 'band']);

/** Item probe used when none is given: every item field is empty. */
const EMPTY_ITEM_PROBE = Object.freeze({isFilled: () => false});

/**
 * @typedef {import('../../shell/types/user.types.js').TemplateComposition} TemplateComposition
 * @typedef {import('../../shell/types/user.types.js').CompositionSection} CompositionSection
 * @typedef {import('../../shell/types/user.types.js').CompositionState} CompositionState
 * @typedef {import('../../shell/types/user.types.js').CompositionItemChoice} CompositionItemChoice
 * @typedef {Object<string, Array<Object<string, string>>>} CompositionLists Item lists keyed by `repeatable.dataKey`
 */

/**
 * @param {TemplateComposition | null | undefined} composition
 * @returns {boolean}
 */
export function hasComposition(composition) {
    return Array.isArray(composition?.sections) && composition.sections.length > 0;
}

/**
 * @param {CompositionSection} section
 * @param {string} layoutId
 */
export function appearanceApplies(section, layoutId) {
    if (!hasAppearanceChoice(section)) return false;
    return !Array.isArray(section.appearanceLayouts) || section.appearanceLayouts.includes(layoutId);
}

/**
 * @param {CompositionSection} section
 * @returns {boolean} whether the section is a blueprint for items rather than one block
 */
export function isRepeatable(section) {
    return Boolean(section.repeatable);
}

/**
 * @param {string} sectionId
 * @param {string} itemId
 * @returns {string} the item reference, e.g. `note:ab1`
 */
export function itemRef(sectionId, itemId) {
    return `${sectionId}${ITEM_REF_SEPARATOR}${itemId}`;
}

/**
 * @param {string} entry A plain section id or an item reference
 * @returns {{sectionId: string, itemId: string | null}} `itemId` is null for a plain section
 */
export function parseEntry(entry) {
    const index = entry.indexOf(ITEM_REF_SEPARATOR);
    if (index === -1) return {sectionId: entry, itemId: null};
    return {sectionId: entry.slice(0, index), itemId: entry.slice(index + 1)};
}

/**
 * Default state of a page. A repeatable section expands to one entry per item of its list in
 * `lists`, in list order. A repeatable section without a list (a new page) gets its
 * `initialItems` (falling back to `minItems`, then 0) blank items with ids from `newId`; the
 * created lists are added to the returned `lists`, which otherwise hold the given lists as is.
 *
 * @param {TemplateComposition} composition
 * @param {CompositionLists} [lists]
 * @param {(taken: string[]) => string} [newId] Returns an item id not in `taken`
 * @returns {{state: CompositionState, lists: CompositionLists}}
 */
export function defaultCompositionState(composition, lists = {}, newId = newRowId) {
    const resultLists = {...lists};
    const state = buildDefaults(composition, (section) => {
        const {dataKey} = section.repeatable;
        if (!Array.isArray(resultLists[dataKey])) {
            resultLists[dataKey] = blankItems(section.repeatable, newId);
        }
        return usableItemIds(resultLists[dataKey]);
    });
    return {state, lists: resultLists};
}

/**
 * Restores saved state, or the defaults of the given lists when nothing was saved. Saved state
 * that no longer matches the template, or whose item references differ from the `_id`s of the
 * lists, is reported as incompatible rather than silently replaced. Lists holding an object
 * without a valid `_id` or an id twice are incompatible too, with or without saved state, so no
 * item is dropped silently. The defaults shown then expand the usable ids of each list (valid,
 * first occurrence only). A missing list counts as empty; restoring never creates items. A saved
 * state that lacks a newly declared plain section gets it with its default choice, after the
 * sections declared before it, never inside a shared row.
 *
 * @param {TemplateComposition} composition
 * @param {unknown} saved
 * @param {CompositionLists} [lists]
 * @param {(key: string) => boolean} [isChecked] Current visibility of optional sections
 * @param {boolean} [validateRows] False only while carrying an already-open editor through a form rebuild,
 *   before its checkbox controls are restored
 * @returns {{state: CompositionState, compatible: boolean}}
 */
export function restoreCompositionState(composition, saved, lists = {}, isChecked = () => true, validateRows = true) {
    const listOf = (section) => lists[section.repeatable.dataKey];
    const defaults = () => buildDefaults(composition, (section) => usableItemIds(listOf(section)));
    if (saved === undefined || saved === null) {
        const usable = composition.sections.filter(isRepeatable)
            .every((section) => strictItemIds(listOf(section)) !== null);
        return {state: defaults(), compatible: usable};
    }
    const parsed = withAddedSections(composition, parseSaved(saved), listOf, isChecked, validateRows);
    if (!parsed || !isCompatible(composition, parsed, listOf, isChecked, validateRows)) {
        return {state: defaults(), compatible: false};
    }
    const sections = {};
    for (const section of composition.sections) {
        if (!isRepeatable(section)) sections[section.id] = copyChoice(parsed.sections[section.id]);
    }
    const state = {version: SUPPORTED_VERSION, order: [...parsed.order], sections};
    if (!hasRepeatable(composition)) return {state, compatible: true};
    const items = {};
    for (const entry of parsed.order) {
        if (parseEntry(entry).itemId !== null) items[entry] = copyChoice(parsed.items[entry]);
    }
    return {state: {...state, items}, compatible: true};
}

/**
 * The declared plain sections a saved state lists in neither `order` nor `sections`: sections
 * added to the template after the page was saved. Empty unless `order` is an array and `sections`
 * an object.
 */
function addedSections(composition, parsed) {
    if (!Array.isArray(parsed?.order) || !isPlainObject(parsed.sections)) return [];
    return composition.sections.filter((section) => !isRepeatable(section)
        && !parsed.order.includes(section.id) && !Object.hasOwn(parsed.sections, section.id));
}

/**
 * Mirrors the backend `PageCompositionService.withAddedSections`: when the saved state is
 * compatible with the template minus its added sections (rows included), each added section, in
 * declaration order, is inserted right after the last entry whose section is declared before it
 * (at the start when there is none), pushed past a shared row it would split, with its default
 * choice. Otherwise the saved state is returned as is, so it fails against the full template.
 * Never mutates `parsed`.
 */
function withAddedSections(composition, parsed, listOf, isChecked, validateRows) {
    const added = addedSections(composition, parsed);
    if (added.length === 0) return parsed;
    const known = {...composition, sections: composition.sections.filter((section) => !added.includes(section))};
    if (!isCompatible(known, parsed, listOf, isChecked, validateRows)) return parsed;
    return added.reduce((state, section) => insertSection(composition, state, section, isChecked), parsed);
}

function insertSection(composition, state, section, isChecked) {
    const declared = composition.sections.map((candidate) => candidate.id);
    const rank = declared.indexOf(section.id);
    const after = state.order.findLastIndex((entry) => declared.indexOf(parseEntry(entry).sectionId) < rank);
    const index = rowSafeInsertIndex(composition, state, isChecked, after + 1);
    const order = [...state.order];
    order.splice(index, 0, section.id);
    return {...state, order, sections: {...state.sections, [section.id]: defaultChoice(section)}};
}

/**
 * @param {TemplateComposition} composition
 * @param {(section: CompositionSection) => string[]} itemIdsOf Item ids of a repeatable section
 * @returns {CompositionState}
 */
function buildDefaults(composition, itemIdsOf) {
    const order = [];
    const sections = {};
    const items = {};
    for (const section of composition.sections) {
        if (isRepeatable(section)) {
            for (const itemId of itemIdsOf(section)) {
                const ref = itemRef(section.id, itemId);
                order.push(ref);
                items[ref] = defaultChoice(section);
            }
        } else {
            order.push(section.id);
            sections[section.id] = defaultChoice(section);
        }
    }
    const state = {version: SUPPORTED_VERSION, order, sections};
    return hasRepeatable(composition) ? {...state, items} : state;
}

/**
 * @param {import('../../shell/types/user.types.js').CompositionRepeatable} repeatable
 * @param {(taken: string[]) => string} newId
 * @returns {Array<Object<string, string>>}
 */
function blankItems(repeatable, newId) {
    const count = repeatable.initialItems ?? repeatable.minItems ?? 0;
    const taken = [];
    const items = [];
    for (let i = 0; i < count; i++) {
        const id = newId(taken);
        taken.push(id);
        items.push(blankItem(repeatable, id));
    }
    return items;
}

/**
 * A new item without content: every field and image companion is `""`, so an image's focal
 * point is empty (section templates render an empty focal point centered).
 *
 * @param {import('../../shell/types/user.types.js').CompositionRepeatable} repeatable
 * @param {string} itemId
 * @returns {Object<string, string>}
 */
export function blankItem(repeatable, itemId) {
    const item = {[TABLE_ROW_ID_KEY]: itemId};
    for (const field of repeatable.fields) {
        item[field.key] = '';
        if (field.type === FieldType.IMAGE) {
            for (const companion of imageCompanionKeys(field.key)) item[companion] = '';
        }
    }
    return item;
}

/**
 * The item objects of a list, as the backend reads it: entries that are not objects are skipped,
 * and a value that is not an array holds none.
 * @param {unknown} list
 * @returns {Object[]}
 */
function itemObjects(list) {
    return Array.isArray(list) ? list.filter(isPlainObject) : [];
}

function isValidItemId(id) {
    return typeof id === 'string' && TABLE_ROW_ID_PATTERN.test(id);
}

/**
 * The valid `_id`s of a list's items in list order, each once: the same filtering the backend's
 * `listIds` applies before it decides whether to accept the list.
 * @param {unknown} list
 * @returns {string[]}
 */
function usableItemIds(list) {
    const ids = itemObjects(list).map((item) => item[TABLE_ROW_ID_KEY]).filter(isValidItemId);
    return [...new Set(ids)];
}

/**
 * Mirrors the backend `listIds`: the `_id`s of a list's items when every item has a valid one
 * and none repeats, else null.
 * @param {unknown} list
 * @returns {string[] | null}
 */
function strictItemIds(list) {
    const ids = itemObjects(list).map((item) => item[TABLE_ROW_ID_KEY]);
    return ids.every(isValidItemId) && new Set(ids).size === ids.length ? ids : null;
}

function hasRepeatable(composition) {
    return composition.sections.some(isRepeatable);
}

function hasAppearanceChoice(section) {
    return Array.isArray(section.appearances) && section.appearances.length > 0;
}

/**
 * @param {CompositionSection} section
 * @returns {CompositionItemChoice}
 */
function defaultChoice(section) {
    const choice = {layout: section.defaultLayout};
    if (hasAppearanceChoice(section)) {
        choice.appearance = section.defaultAppearance;
    }
    return choice;
}

function copyChoice(choice) {
    const copy = {layout: choice.layout};
    for (const key of ['appearance', 'span', 'band']) {
        if (choice[key] !== undefined) copy[key] = choice[key];
    }
    return copy;
}

function parseSaved(saved) {
    if (typeof saved === 'string') {
        try {
            return JSON.parse(saved);
        } catch {
            return null;
        }
    }
    return typeof saved === 'object' ? saved : null;
}

function isPlainObject(value) {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isCompatible(composition, saved, listOf, isChecked, validateRows) {
    if (!saved || saved.version !== SUPPORTED_VERSION || !Array.isArray(saved.order)) return false;
    if (!isPlainObject(saved.sections)) return false;
    const repeatable = hasRepeatable(composition);
    const allowedKeys = repeatable ? REPEATABLE_STATE_KEYS : STATE_KEYS;
    if (!Object.keys(saved).every((key) => allowedKeys.includes(key))) return false;
    const choicesFit = repeatable
        ? isPlainObject(saved.items) && isRepeatableCompatible(composition, saved, listOf)
        : isPlainCompatible(composition, saved);
    return choicesFit && (!validateRows || compositionRows(composition, saved, isChecked).every((row) => row.valid));
}

function isPlainCompatible(composition, saved) {
    const ids = composition.sections.map((section) => section.id);
    if (saved.order.length !== ids.length || new Set(saved.order).size !== ids.length) return false;
    if (!saved.order.every((id) => ids.includes(id))) return false;
    if (Object.keys(saved.sections).some((id) => !ids.includes(id))) return false;
    const bandIds = bandIdsOf(composition);
    return composition.sections.every((section) => isValidChoice(section, saved.sections[section.id], bandIds));
}

function isRepeatableCompatible(composition, saved, listOf) {
    if (!saved.order.every((entry) => typeof entry === 'string')) return false;
    if (new Set(saved.order).size !== saved.order.length) return false;
    const byId = sectionsById(composition);
    const entryIsValid = (entry) => {
        const section = entrySection(byId, entry);
        const {itemId} = parseEntry(entry);
        return section !== undefined && (itemId === null || isValidItemId(itemId));
    };
    if (!saved.order.every(entryIsValid)) return false;

    const plain = composition.sections.filter((section) => !isRepeatable(section));
    const plainIds = plain.map((section) => section.id);
    const refs = saved.order.filter((entry) => parseEntry(entry).itemId !== null);
    if (saved.order.length - refs.length !== plain.length) return false;
    if (Object.keys(saved.sections).some((id) => !plainIds.includes(id))) return false;
    const bandIds = bandIdsOf(composition);
    if (!plain.every((section) => isValidChoice(section, saved.sections[section.id], bandIds))) return false;

    if (Object.keys(saved.items).length !== refs.length) return false;
    if (!refs.every((ref) => isValidChoice(byId.get(parseEntry(ref).sectionId), saved.items[ref], bandIds))) return false;

    return composition.sections.filter(isRepeatable).every((section) => {
        const ids = strictItemIds(listOf(section));
        const refIds = new Set(refs.filter((ref) => isItemOf(ref, section.id)).map((ref) => parseEntry(ref).itemId));
        return ids !== null && ids.length === refIds.size && ids.every((id) => refIds.has(id));
    });
}

function isValidChoice(section, choice, bandIds) {
    if (!isPlainObject(choice)) return false;
    if (!Object.keys(choice).every((key) => CHOICE_KEYS.includes(key))) return false;
    if (!section.layouts.some((layout) => layout.id === choice.layout)) return false;
    if (choice.span !== undefined && !isValidSpan(section, choice.span)) return false;
    if (choice.band !== undefined && !bandIds.includes(choice.band)) return false;
    if (!hasAppearanceChoice(section)) return choice.appearance === undefined;
    return section.appearances.some((appearance) => appearance.id === choice.appearance);
}

function isValidSpan(section, span) {
    return hasSpan(section) && Number.isInteger(span) && span >= minSpan(section) && span <= CompositionGrid.COLUMNS;
}

function bandIdsOf(composition) {
    return hasBands(composition) ? composition.bands.map((band) => band.id) : [];
}

function sectionsById(composition) {
    return new Map(composition.sections.map((section) => [section.id, section]));
}

/**
 * The declared section an entry belongs to, or undefined when the section is unknown or the
 * entry's kind does not match it (an item reference to a plain section, or a bare repeatable id).
 */
function entrySection(byId, entry) {
    const {sectionId, itemId} = parseEntry(entry);
    const section = byId.get(sectionId);
    return section && isRepeatable(section) === (itemId !== null) ? section : undefined;
}

function isItemOf(entry, sectionId) {
    const parsed = parseEntry(entry);
    return parsed.itemId !== null && parsed.sectionId === sectionId;
}

/**
 * The choice of an entry: from `items` for an item reference, from `sections` otherwise.
 *
 * @param {CompositionState} state
 * @param {string} entry
 * @returns {CompositionItemChoice | undefined}
 */
export function entryChoice(state, entry) {
    return parseEntry(entry).itemId === null ? state.sections[entry] : state.items?.[entry];
}

/** Applies `patch` to an entry's choice; a key patched to `undefined` is removed. */
function updateChoice(state, entry, patch) {
    const choice = {...entryChoice(state, entry)};
    for (const [key, value] of Object.entries(patch)) {
        if (value === undefined) {
            delete choice[key];
        } else {
            choice[key] = value;
        }
    }
    if (parseEntry(entry).itemId === null) {
        return {...state, sections: {...state.sections, [entry]: choice}};
    }
    return {...state, items: {...state.items, [entry]: choice}};
}

/** The full width is stored as the absence of `span`, so templates without rows keep their shape. */
function spanPatch(span) {
    return span === CompositionGrid.COLUMNS ? undefined : span;
}

/**
 * Number of items a repeatable section has in the state.
 *
 * @param {CompositionState} state
 * @param {string} sectionId
 * @returns {number}
 */
export function itemCount(state, sectionId) {
    return state.order.filter((entry) => isItemOf(entry, sectionId)).length;
}

/**
 * Choice for a new item inserted at `insertIndex`: the opposite of the nearest item of the same
 * section above it (the other layout when the section has exactly two layouts, else the same;
 * likewise for appearances), or the section defaults when there is none above.
 *
 * @param {CompositionState} state
 * @param {TemplateComposition} composition
 * @param {string} sectionId
 * @param {number} insertIndex
 * @returns {CompositionItemChoice}
 */
export function proposeItemChoice(state, composition, sectionId, insertIndex) {
    const section = sectionsById(composition).get(sectionId);
    const above = state.order.slice(0, insertIndex).findLast((entry) => isItemOf(entry, sectionId));
    if (above === undefined) return defaultChoice(section);
    const nearest = state.items[above];
    const choice = {layout: alternate(section.layouts.map((layout) => layout.id), nearest.layout)};
    if (hasAppearanceChoice(section)) {
        choice.appearance = alternate(section.appearances.map((appearance) => appearance.id), nearest.appearance);
    }
    return choice;
}

function alternate(ids, current) {
    return ids.length === 2 ? ids.find((id) => id !== current) : current;
}

/**
 * Adds a new item: after the last item of the same section, or, when it has none, before the
 * first entry whose section is declared after it, else at the end; pushed past a shared row when
 * that position would otherwise land inside it. Its choice is {@link proposeItemChoice} for that
 * (possibly pushed) position.
 *
 * @param {CompositionState} state
 * @param {TemplateComposition} composition
 * @param {string} sectionId
 * @param {string} itemId
 * @param {(key: string) => boolean} [isChecked] Defaults to treating every section as visible
 * @returns {CompositionState}
 */
export function addItem(state, composition, sectionId, itemId, isChecked = () => true) {
    const declared = composition.sections.map((section) => section.id);
    const rank = declared.indexOf(sectionId);
    const lastOwn = state.order.findLastIndex((entry) => isItemOf(entry, sectionId));
    const later = state.order.findIndex((entry) => declared.indexOf(parseEntry(entry).sectionId) > rank);
    let index = state.order.length;
    if (lastOwn !== -1) {
        index = lastOwn + 1;
    } else if (later !== -1) {
        index = later;
    }
    index = rowSafeInsertIndex(composition, state, isChecked, index);
    return insertItem(state, index, itemRef(sectionId, itemId), proposeItemChoice(state, composition, sectionId, index));
}

/**
 * `index` unless it lands strictly inside a shared row, in which case the index right after that
 * row's last entry, so an inserted entry never splits a row. The row is the one of the nearest
 * entry before `index` that is in a row; entries in no row (disabled sections) are skipped, since
 * a row's blocks can sit on either side of them in the order. `index` unchanged when no entry
 * before it is in a row, or when that row ends before `index` (a full-width entry, or the second
 * block of a shared row). Mirrors the backend `PageCompositionService.rowSafeIndex`.
 */
function rowSafeInsertIndex(composition, state, isChecked, index) {
    const rows = compositionRows(composition, state, isChecked);
    const rowOf = (entry) => rows.find((candidate) => candidate.entries.includes(entry));
    const before = state.order.slice(0, index).findLast((entry) => rowOf(entry) !== undefined);
    if (before === undefined) return index;
    return Math.max(index, state.order.indexOf(rowOf(before).entries.at(-1)) + 1);
}

/**
 * Inserts a copy of an item after `after` (the item itself by default), with the same layout and
 * appearance at the full width: a copy never joins a row or sets a band.
 *
 * @param {CompositionState} state
 * @param {string} ref
 * @param {string} newItemId
 * @param {string} [after]
 * @returns {CompositionState} the same state when `ref` or `after` is not in the order
 */
export function duplicateItem(state, ref, newItemId, after = ref) {
    const index = state.order.indexOf(after);
    if (index === -1 || !state.order.includes(ref)) return state;
    const {span: _span, band: _band, ...choice} = state.items[ref];
    return insertItem(state, index + 1, itemRef(parseEntry(ref).sectionId, newItemId), choice);
}

function insertItem(state, index, ref, choice) {
    const order = [...state.order];
    order.splice(index, 0, ref);
    return {...state, order, items: {...state.items, [ref]: choice}};
}

/**
 * @param {CompositionState} state
 * @param {string} ref
 * @returns {CompositionState} the same state when `ref` is not in the order
 */
export function removeItem(state, ref) {
    if (!state.order.includes(ref)) return state;
    const items = {...state.items};
    delete items[ref];
    return {...state, order: state.order.filter((entry) => entry !== ref), items};
}

/**
 * @param {CompositionSection | undefined} section
 * @returns {boolean} whether the section may share a row with a neighbour
 */
export function hasSpan(section) {
    return Number.isInteger(section?.span?.min);
}

/**
 * @param {CompositionSection | undefined} section
 * @returns {number} the fewest columns the section takes in a shared row; the full width without a span
 */
export function minSpan(section) {
    return hasSpan(section) ? section.span.min : CompositionGrid.COLUMNS;
}

/** @param {TemplateComposition} composition */
export function hasBands(composition) {
    return Array.isArray(composition.bands) && composition.bands.length > 0;
}

/**
 * @param {TemplateComposition} composition
 * @returns {boolean} whether any section may share a row
 */
export function hasRowPlacement(composition) {
    return composition.sections.some(hasSpan);
}

/**
 * @param {CompositionState} state
 * @param {string} entry
 * @returns {number} the entry's span, or the full width
 */
export function entrySpan(state, entry) {
    return entryChoice(state, entry)?.span ?? CompositionGrid.COLUMNS;
}

/** The entries of the order whose section is declared and enabled, in page order. */
function enabledEntries(composition, state, isChecked) {
    const byId = sectionsById(composition);
    return state.order.filter((entry) => {
        const section = entrySection(byId, entry);
        return section !== undefined && isSectionEnabled(section, isChecked);
    });
}

/**
 * @typedef {{entries: string[], total: number, valid: boolean}} CompositionRow
 */

/**
 * Mirrors the backend `CompositionRows`: the enabled entries in page order, grouped into rows.
 * An entry at the full width is a row of its own; entries below it share a row with their direct
 * enabled neighbour, at most `CompositionGrid.MAX_BLOCKS_PER_ROW` per row. A row is valid when
 * its spans add up to exactly `CompositionGrid.COLUMNS` and only its first entry sets a band.
 *
 * @param {TemplateComposition} composition
 * @param {CompositionState} state
 * @param {(key: string) => boolean} isChecked
 * @returns {CompositionRow[]}
 */
export function compositionRows(composition, state, isChecked) {
    const enabled = enabledEntries(composition, state, isChecked);
    const rows = [];
    let start = 0;
    while (start < enabled.length) {
        const entries = [enabled[start]];
        let total = entrySpan(state, enabled[start]);
        let next = start + 1;
        while (total < CompositionGrid.COLUMNS && entries.length < CompositionGrid.MAX_BLOCKS_PER_ROW
            && next < enabled.length && entrySpan(state, enabled[next]) < CompositionGrid.COLUMNS) {
            total += entrySpan(state, enabled[next]);
            entries.push(enabled[next]);
            next++;
        }
        const followerBand = entries.slice(1).some((entry) => entryChoice(state, entry).band !== undefined);
        rows.push({entries, total, valid: total === CompositionGrid.COLUMNS && !followerBand});
        start = next;
    }
    return rows;
}

/** How many colours the editor cycles through to tell shared rows apart. */
export const ROW_TONE_COUNT = 6;

/**
 * The editor colour of every block in a shared row: the shared rows are counted in page order and
 * cycle through {@link ROW_TONE_COUNT} colours, so neighbouring rows always differ. A block alone
 * in its row gets none. The colour is an extra cue next to the row number, never the only one.
 *
 * @param {CompositionRow[]} rows
 * @returns {Map<string, number>} each entry of a shared row to its colour index
 */
export function rowTones(rows) {
    const tones = new Map();
    rows.filter((row) => row.entries.length > 1).forEach((row, index) => {
        for (const entry of row.entries) tones.set(entry, index % ROW_TONE_COUNT);
    });
    return tones;
}

/**
 * Brings the rows back in line with the row rules after the order or the visible entries
 * changed, without ever creating a row: pairing is only made by an explicit action. It walks
 * the enabled entries in page order: an entry below the full width keeps sharing a row with the
 * next enabled entry when that one is below the full width too, their spans add up to exactly
 * `CompositionGrid.COLUMNS` and the same two entries were a row in `previousRows`. When the change
 * only reversed them, the row stays as after a swap: spans stay with their blocks and the band
 * moves to the new first block; a band on the second block is dropped either way. Otherwise only
 * that entry returns to the full width, keeping its own band because it now stands alone, and the
 * walk continues with the next entry. Rows that still hold are never touched by a change
 * elsewhere on the page.
 *
 * @param {TemplateComposition} composition
 * @param {CompositionState} state
 * @param {(key: string) => boolean} isChecked
 * @param {CompositionRow[]} previousRows The rows before the change, as {@link compositionRows}
 *   gave them for the previous state and visibility
 * @returns {{state: CompositionState, dissolved: string[][]}} each entry returned to the full width, as a list of its own
 */
export function repairRows(composition, state, isChecked, previousRows) {
    const wasRow = (first, second) => previousRows.some(({entries}) => entries[0] === first && entries[1] === second);
    const enabled = enabledEntries(composition, state, isChecked);
    let next = state;
    const dissolved = [];
    let index = 0;
    while (index < enabled.length) {
        const entry = enabled[index];
        const span = entrySpan(next, entry);
        if (span === CompositionGrid.COLUMNS) {
            index++;
            continue;
        }
        const partner = enabled[index + 1];
        const partnerSpan = partner === undefined ? CompositionGrid.COLUMNS : entrySpan(next, partner);
        const kept = wasRow(entry, partner) || wasRow(partner, entry);
        if (partnerSpan < CompositionGrid.COLUMNS && span + partnerSpan === CompositionGrid.COLUMNS && kept) {
            const {band} = entryChoice(next, wasRow(entry, partner) ? entry : partner);
            next = updateChoice(updateChoice(next, partner, {band: undefined}), entry, {band});
            index += 2;
            continue;
        }
        next = updateChoice(next, entry, {span: undefined});
        dissolved.push([entry]);
        index++;
    }
    return {state: next, dissolved};
}

/**
 * The entry that `entry` can be paired with: the one alone in the row directly above it, when
 * `entry` is alone in its row too, both sections declare a span and their minimums fit one row.
 *
 * @returns {string | null}
 */
export function pairablePrevious(composition, state, entry, isChecked) {
    const rows = compositionRows(composition, state, isChecked);
    const index = rows.findIndex((row) => row.entries.includes(entry));
    if (index < 1 || rows[index].entries.length !== 1 || rows[index - 1].entries.length !== 1) return null;
    const [previous] = rows[index - 1].entries;
    const byId = sectionsById(composition);
    const first = entrySection(byId, previous);
    const second = entrySection(byId, entry);
    if (!hasSpan(first) || !hasSpan(second) || minSpan(first) + minSpan(second) > CompositionGrid.COLUMNS) return null;
    return previous;
}

/**
 * Puts `entry` next to the block above it at the split closest to even; the state is unchanged
 * when {@link pairablePrevious} finds no partner.
 */
export function pairWithPrevious(composition, state, entry, isChecked) {
    const previous = pairablePrevious(composition, state, entry, isChecked);
    if (previous === null) return state;
    return setSplit(composition, updateChoice(state, entry, {band: undefined}), previous, entry, CompositionGrid.COLUMNS / 2);
}

/**
 * @returns {{min: number, max: number}} the spans the first block of a row may take
 */
export function splitBounds(composition, first, second) {
    const byId = sectionsById(composition);
    return {
        min: minSpan(byId.get(parseEntry(first).sectionId)),
        max: CompositionGrid.COLUMNS - minSpan(byId.get(parseEntry(second).sectionId)),
    };
}

/** Sets the first block of a row to `firstSpan`, clamped to both minimums, and the second to the rest. */
export function setSplit(composition, state, first, second, firstSpan) {
    const {min, max} = splitBounds(composition, first, second);
    const span = Math.min(max, Math.max(min, Math.round(firstSpan)));
    return updateChoice(updateChoice(state, first, {span: spanPatch(span)}), second,
        {span: spanPatch(CompositionGrid.COLUMNS - span)});
}

/** Swaps the two blocks of a row; spans stay with their blocks and the band moves to the new first block. */
export function swapRow(state, first, second) {
    const a = state.order.indexOf(first);
    const b = state.order.indexOf(second);
    if (a === -1 || b === -1) return state;
    const order = [...state.order];
    order[a] = second;
    order[b] = first;
    const {band} = entryChoice(state, first);
    return updateChoice(updateChoice({...state, order}, first, {band: undefined}), second, {band});
}

/** Returns the entries of a row to the full width; the first keeps its band. */
export function dissolveRow(state, entries) {
    return entries.reduce((next, entry, index) =>
        updateChoice(next, entry, index === 0 ? {span: undefined} : {span: undefined, band: undefined}), state);
}

/** Chooses the band of the row that `entry` starts. */
export function selectBand(state, entry, bandId) {
    return updateChoice(state, entry, {band: bandId});
}

/** The band a row renders with: its first block's choice, else the composition default. */
export function rowBand(composition, state, firstEntry) {
    return entryChoice(state, firstEntry).band ?? composition.defaultBand;
}

/** Inserts a new item at `index` with the choice {@link proposeItemChoice} makes for that position. */
export function insertItemAt(state, composition, sectionId, itemId, index) {
    return insertItem(state, index, itemRef(sectionId, itemId), proposeItemChoice(state, composition, sectionId, index));
}

/**
 * @param {import('../../shell/types/user.types.js').CompositionState} state
 * @param {string} sectionId
 * @param {number} targetIndex
 */
export function moveSectionTo(state, sectionId, targetIndex) {
    const currentIndex = state.order.indexOf(sectionId);
    if (currentIndex === -1) return state;
    const clamped = Math.max(0, Math.min(state.order.length - 1, targetIndex));
    if (clamped === currentIndex) return state;
    const order = state.order.filter((id) => id !== sectionId);
    order.splice(clamped, 0, sectionId);
    return {...state, order};
}

/**
 * @param {import('../../shell/types/user.types.js').CompositionState} state
 * @param {string} sectionId
 * @param {number} delta
 */
export function moveSection(state, sectionId, delta) {
    return moveSectionTo(state, sectionId, state.order.indexOf(sectionId) + delta);
}

/**
 * @param {CompositionState} state
 * @param {string} entry A plain section id or an item reference
 * @param {string} layoutId
 */
export function selectLayout(state, entry, layoutId) {
    return updateChoice(state, entry, {layout: layoutId});
}

/**
 * Changing appearance keeps the choice even while a layout without appearance is selected, so
 * switching back restores it.
 *
 * @param {CompositionState} state
 * @param {string} entry A plain section id or an item reference
 * @param {string} appearanceId
 */
export function selectAppearance(state, entry, appearanceId) {
    return updateChoice(state, entry, {appearance: appearanceId});
}

/**
 * The `wb_composition` wire value. `items` is only part of it for templates with a repeatable
 * section, because only their states carry it.
 *
 * @param {CompositionState} state
 * @returns {string}
 */
export function serializeCompositionState(state) {
    return JSON.stringify(state);
}

/**
 * @param {import('../../shell/types/user.types.js').CompositionSection} section
 * @param {string} layoutId
 */
export function layoutFor(section, layoutId) {
    return section.layouts.find((layout) => layout.id === layoutId) ?? section.layouts[0];
}

/**
 * Field keys rendered inside a section's card: its declared fields, the image companions
 * (`{key}_alt`, `{key}_position`) of its image fields, and its visibility checkbox.
 *
 * @param {import('../../shell/types/user.types.js').TemplateComposition} composition
 * @param {import('../../shell/types/user.types.js').TemplateField[]} fields
 * @returns {Map<string, string>} field key → section id
 */
export function fieldOwnership(composition, fields) {
    const typeByKey = new Map((fields || []).map((field) => [field.key, field.type]));
    const owners = new Map();
    for (const section of composition.sections) {
        for (const key of section.fields || []) {
            owners.set(key, section.id);
            if (typeByKey.get(key) === FieldType.IMAGE) {
                for (const companion of imageCompanionKeys(key)) owners.set(companion, section.id);
            }
        }
        if (section.enabledByField) {
            owners.set(section.enabledByField, section.id);
        }
    }
    return owners;
}

/**
 * Keys visible in the selected layout, including image companions of visible image fields.
 *
 * @param {import('../../shell/types/user.types.js').CompositionSection} section
 * @param {string} layoutId
 * @param {import('../../shell/types/user.types.js').TemplateField[]} fields
 * @returns {Set<string>}
 */
export function visibleFieldKeys(section, layoutId, fields) {
    const typeByKey = new Map((fields || []).map((field) => [field.key, field.type]));
    const visible = new Set();
    for (const key of layoutFor(section, layoutId).fields || []) {
        visible.add(key);
        if (typeByKey.get(key) === FieldType.IMAGE) {
            for (const companion of imageCompanionKeys(key)) visible.add(companion);
        }
    }
    return visible;
}

/**
 * Required keys for the selected layout: its `requiredFields` plus visible fields marked
 * required in the flat schema (checkboxes excluded).
 *
 * @param {import('../../shell/types/user.types.js').CompositionSection} section
 * @param {string} layoutId
 * @param {import('../../shell/types/user.types.js').TemplateField[]} fields
 * @param {(key: string) => boolean} [isFilled]
 * @returns {string[]}
 */
export function requiredFieldKeys(section, layoutId, fields, isFilled = () => true) {
    const layout = layoutFor(section, layoutId);
    const byKey = new Map((fields || []).map((field) => [field.key, field]));
    const required = new Set(layout.requiredFields || []);
    for (const key of layout.fields || []) {
        const field = byKey.get(key);
        if (field?.required && field.type !== FieldType.CHECKBOX) {
            required.add(key);
        }
    }
    return [...required].filter((key) => {
        const dependency = byKey.get(key)?.dependsOn;
        return !dependency || isFilled(dependency);
    });
}

/**
 * @param {import('../../shell/types/user.types.js').CompositionSection} section
 * @param {(key: string) => boolean} isChecked
 */
export function isSectionEnabled(section, isChecked) {
    return !section.enabledByField || isChecked(section.enabledByField);
}

/**
 * Content problems for one section, or one item of a repeatable section, in its selected
 * layout, ignoring whether it is enabled. For an item, pass the item fields, a probe of the
 * item's values and its reference; the problems then carry that `ref`.
 *
 * @param {CompositionSection} section
 * @param {string} layoutId
 * @param {import('../../shell/types/user.types.js').TemplateField[]} fields
 * @param {(key: string) => boolean} isFilled
 * @param {string} [ref] Item reference the problems belong to
 * @returns {import('../../shell/types/user.types.js').CompositionProblem[]}
 */
export function sectionContentProblems(section, layoutId, fields, isFilled, ref) {
    const owner = ref === undefined ? {sectionId: section.id} : {sectionId: section.id, ref};
    const problems = [];
    const missing = requiredFieldKeys(section, layoutId, fields, isFilled).filter((key) => !isFilled(key));
    if (missing.length > 0) {
        problems.push({...owner, type: 'required', keys: missing});
    }
    const anyOf = layoutFor(section, layoutId).requiredAnyFields;
    if (Array.isArray(anyOf) && anyOf.length > 0 && !anyOf.some((key) => isFilled(key))) {
        problems.push({...owner, type: 'requiredAny', keys: [...anyOf]});
    }
    return problems;
}

/**
 * Content problems across all enabled sections and items, in page order. Item fields are
 * checked against the section's `repeatable.fields` through `itemProbe`; entries that match no
 * declared section are skipped.
 *
 * @param {TemplateComposition} composition
 * @param {import('../../shell/types/user.types.js').TemplateField[]} fields
 * @param {CompositionState} state
 * @param {(key: string) => boolean} isFilled
 * @param {(key: string) => boolean} isChecked
 * @param {(ref: string) => {isFilled: (key: string) => boolean}} [itemProbe] Values of one item;
 *   only called for item references. Without it every item field counts as empty.
 * @returns {import('../../shell/types/user.types.js').CompositionProblem[]}
 */
export function validateComposition(composition, fields, state, isFilled, isChecked, itemProbe = () => EMPTY_ITEM_PROBE) {
    const byId = sectionsById(composition);
    return state.order.flatMap((entry) => {
        const section = entrySection(byId, entry);
        if (!section || !isSectionEnabled(section, isChecked)) return [];
        if (!isRepeatable(section)) {
            return sectionContentProblems(section, state.sections[entry].layout, fields, isFilled);
        }
        const probe = itemProbe(entry);
        return sectionContentProblems(section, state.items[entry].layout, section.repeatable.fields,
            (key) => probe.isFilled(key), entry);
    });
}
